import { useEffect, useReducer, useCallback, useRef } from "react";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import useAuth from "../useAuth.js/index.js";

const initialState = {
  tickets: [],
  loading: false,
  hasMore: false,
  count: 0,
  error: null,
};

function reducer(state, action) {
  switch (action.type) {
    case "LOADING":
      return { ...state, loading: true, error: null };

    case "LOAD_TICKETS":
      return {
        ...state,
        loading: false,
        tickets: action.payload.tickets,
        hasMore: action.payload.hasMore,
        count: action.payload.count,
      };

    case "APPEND_TICKETS":
      const existingIds = new Set(state.tickets.map(t => t.id));
      const newTickets = action.payload.tickets.filter(t => !existingIds.has(t.id));

      return {
        ...state,
        loading: false,
        tickets: [...state.tickets, ...newTickets],
        hasMore: action.payload.hasMore,
        count: action.payload.count,
      };

    case "RESET":
      return initialState;

    case "ERROR":
      return { ...state, loading: false, error: action.payload };

    default:
      return state;
  }
}

const useTickets = ({
  searchParam,
  tags,
  users,
  pageNumber = 1,
  status,
  date,
  updatedAt,
  showAll,
  queueIds,
  withUnreadMessages,
  userId,
  queueId,
  dateFrom,
  dateTo,
  connections,
}) => {

  const [state, dispatch] = useReducer(reducer, initialState);
  const { tickets, loading, hasMore, count, error } = state;
  const { user: { profile: userProfile } } = useAuth();
  const isLoadingRef = useRef(false);
  const cacheRef = useRef(new Map());

  const buildRequestKey = () => JSON.stringify({
    searchParam,
    tags,
    users,
    status,
    queueId,
    queueIds,
    connections,
    dateFrom,
    dateTo,
    pageNumber,
    showAll
  });

  const buildParams = () => {
    const params = {};

    if (searchParam?.trim()) params.searchParam = searchParam.trim();
    if (pageNumber) params.pageNumber = pageNumber;
    if (status) params.status = status;

    params.tags = JSON.stringify((tags || []).filter(n => typeof n === "number"));
    params.users = JSON.stringify((users || []).filter(n => typeof n === "number"));
    params.connections = JSON.stringify((connections || []).filter(n => typeof n === "number"));

    if (queueId) {
      params.queueId = queueId;
    } else {
      params.queueIds = JSON.stringify((queueIds || []).filter(n => typeof n === "number"));
    }

    if (date) params.date = date;
    if (updatedAt) params.updatedAt = updatedAt;
    if (showAll !== undefined) params.showAll = showAll;
    if (withUnreadMessages) params.withUnreadMessages = withUnreadMessages;
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;

    return params;
  };

  const fetchTickets = useCallback(async ({ force = false } = {}) => {
    if (!userProfile) return;

    if (isLoadingRef.current) return;

    const requestKey = buildRequestKey();
    const cached = cacheRef.current.get(requestKey);

    // ✅ usar cache si no es forzado
    if (!force && cached && Date.now() - cached.timestamp < 5000) {
      dispatch({
        type: pageNumber === 1 ? "LOAD_TICKETS" : "APPEND_TICKETS",
        payload: cached.data,
      });
      return;
    }

    dispatch({ type: "LOADING" });
    isLoadingRef.current = true;

    try {
      const params = buildParams();

      const { data } = await api.get("/tickets", { params: { ...params, userProfile } });

      const actionType = pageNumber === 1 ? "LOAD_TICKETS" : "APPEND_TICKETS";

      dispatch({
        type: actionType,
        payload: {
          tickets: data.tickets,
          hasMore: data.hasMore,
          count: data.count,
        },
      });

      cacheRef.current.set(requestKey, {
        timestamp: Date.now(),
        data: {
          tickets: data.tickets,
          hasMore: data.hasMore,
          count: data.count,
        }
      });

      // limpiar cache viejo
      if (cacheRef.current.size > 10) {
        const entries = [...cacheRef.current.entries()]
          .sort((a, b) => b[1].timestamp - a[1].timestamp)
          .slice(0, 10);

        cacheRef.current.clear();
        entries.forEach(([k, v]) => cacheRef.current.set(k, v));
      }

    } catch (err) {
      console.error("Error fetching tickets:", err);
      toastError(err);
      dispatch({ type: "ERROR", payload: err.message });
    } finally {
      isLoadingRef.current = false;
    }

  }, [
    searchParam,
    pageNumber,
    status,
    tags,
    users,
    connections,
    date,
    updatedAt,
    showAll,
    queueIds,
    withUnreadMessages,
    queueId,
    dateFrom,
    dateTo,
    userProfile,
  ]);

  const reloadTickets = useCallback(() => {
    cacheRef.current.clear();
    dispatch({ type: "RESET" });

    setTimeout(() => {
      fetchTickets({ force: true });
    }, 0);
  }, [fetchTickets]);

  const resetTickets = useCallback(() => {
    cacheRef.current.clear();
    dispatch({ type: "RESET" });
  }, []);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  return {
    tickets,
    loading,
    hasMore,
    count,
    error,
    reloadTickets,
    resetTickets
  };
};

export default useTickets;